import type { ApiClient } from "../auth/apiClient";
import {
    ManagementGroupListResponseSchema,
    ManagementGroupSchema,
    TeamQueuePushResponseSchema,
    TeamQueueWithdrawResponseSchema,
    TeamSchema,
} from "./types";
import type {
    ManagementGroup,
    ManagementGroupListResponse,
    Team,
    TeamQueuePushResponse,
    TeamQueueWithdrawResponse,
} from "./types";

export async function fetchManagementGroups(
    client: ApiClient,
    includeDeleted = false,
): Promise<ManagementGroupListResponse> {
    const path = includeDeleted ? "/management-groups?includeDeleted=true" : "/management-groups";
    const response = await client.fetch(path);
    if (!response.ok) {
        throw new Error(
            `Failed to fetch management groups: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return ManagementGroupListResponseSchema.parse(json);
}

export async function fetchManagementGroup(
    client: ApiClient,
    id: string,
): Promise<ManagementGroup> {
    const response = await client.fetch(`/management-groups/${encodeURIComponent(id)}`);
    if (!response.ok) {
        throw new Error(
            `Failed to fetch management group ${id}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return ManagementGroupSchema.parse(json);
}

export interface CreateManagementGroupInput {
    readonly name: string;
}

export async function createManagementGroup(
    client: ApiClient,
    input: CreateManagementGroupInput,
): Promise<ManagementGroup> {
    const response = await client.fetch("/management-groups", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: input.name }),
    });
    if (!response.ok) {
        throw new Error(
            `Failed to create management group: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return ManagementGroupSchema.parse(json);
}

export interface RenameManagementGroupInput {
    readonly id: string;
    readonly name: string;
}

export async function renameManagementGroup(
    client: ApiClient,
    input: RenameManagementGroupInput,
): Promise<ManagementGroup> {
    const response = await client.fetch(`/management-groups/${encodeURIComponent(input.id)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: input.name }),
    });
    if (!response.ok) {
        throw new Error(
            `Failed to rename management group: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return ManagementGroupSchema.parse(json);
}

export async function deleteManagementGroup(
    client: ApiClient,
    id: string,
): Promise<void> {
    const response = await client.fetch(`/management-groups/${encodeURIComponent(id)}`, {
        method: "DELETE",
    });
    if (!response.ok) {
        throw new Error(
            `Failed to delete management group: ${String(response.status)} ${response.statusText}`,
        );
    }
}

export interface AddAdminInput {
    readonly id: string;
    readonly userId: string;
    readonly displayName: string;
}

export async function addManagementGroupAdmin(
    client: ApiClient,
    input: AddAdminInput,
): Promise<ManagementGroup> {
    const response = await client.fetch(
        `/management-groups/${encodeURIComponent(input.id)}/admins`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId: input.userId, displayName: input.displayName }),
        },
    );
    if (!response.ok) {
        throw new Error(
            `Failed to add admin: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return ManagementGroupSchema.parse(json);
}

export interface RemoveAdminInput {
    readonly id: string;
    readonly userId: string;
}

export async function removeManagementGroupAdmin(
    client: ApiClient,
    input: RemoveAdminInput,
): Promise<ManagementGroup> {
    const response = await client.fetch(
        `/management-groups/${encodeURIComponent(input.id)}/admins/${encodeURIComponent(input.userId)}`,
        { method: "DELETE" },
    );
    if (!response.ok) {
        throw new Error(
            `Failed to remove admin: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return ManagementGroupSchema.parse(json);
}

export interface SetOwnedScopesInput {
    readonly id: string;
    readonly scopes: readonly { kind: string; entityType?: string | null }[];
}

export async function setManagementGroupScopes(
    client: ApiClient,
    input: SetOwnedScopesInput,
): Promise<ManagementGroup> {
    const response = await client.fetch(
        `/management-groups/${encodeURIComponent(input.id)}/owned-scopes`,
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                scopes: input.scopes.map((s) => ({
                    kind: s.kind,
                    entityType: s.entityType ?? null,
                })),
            }),
        },
    );
    if (!response.ok) {
        throw new Error(
            `Failed to set owned scopes: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return ManagementGroupSchema.parse(json);
}

export interface CreateTeamInput {
    readonly id: string;
    readonly name: string;
}

export async function createTeam(
    client: ApiClient,
    input: CreateTeamInput,
): Promise<Team> {
    const response = await client.fetch(
        `/management-groups/${encodeURIComponent(input.id)}/teams`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: input.name }),
        },
    );
    if (!response.ok) {
        throw new Error(
            `Failed to create team: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return TeamSchema.parse(json);
}

export interface RenameTeamInput {
    readonly id: string;
    readonly teamId: string;
    readonly name: string;
}

export async function renameTeam(
    client: ApiClient,
    input: RenameTeamInput,
): Promise<Team> {
    const response = await client.fetch(
        `/management-groups/${encodeURIComponent(input.id)}/teams/${encodeURIComponent(input.teamId)}`,
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: input.name }),
        },
    );
    if (!response.ok) {
        throw new Error(
            `Failed to rename team: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return TeamSchema.parse(json);
}

export interface DeleteTeamInput {
    readonly id: string;
    readonly teamId: string;
}

export async function deleteTeam(
    client: ApiClient,
    input: DeleteTeamInput,
): Promise<void> {
    const response = await client.fetch(
        `/management-groups/${encodeURIComponent(input.id)}/teams/${encodeURIComponent(input.teamId)}`,
        { method: "DELETE" },
    );
    if (!response.ok) {
        throw new Error(
            `Failed to delete team: ${String(response.status)} ${response.statusText}`,
        );
    }
}

export interface AddTeamMemberInput {
    readonly id: string;
    readonly teamId: string;
    readonly userId: string;
    readonly displayName: string;
}

export async function addTeamMember(
    client: ApiClient,
    input: AddTeamMemberInput,
): Promise<Team> {
    const response = await client.fetch(
        `/management-groups/${encodeURIComponent(input.id)}/teams/${encodeURIComponent(input.teamId)}/members`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId: input.userId, displayName: input.displayName }),
        },
    );
    if (!response.ok) {
        throw new Error(
            `Failed to add team member: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return TeamSchema.parse(json);
}

export interface RemoveTeamMemberInput {
    readonly id: string;
    readonly teamId: string;
    readonly userId: string;
}

export async function removeTeamMember(
    client: ApiClient,
    input: RemoveTeamMemberInput,
): Promise<Team> {
    const response = await client.fetch(
        `/management-groups/${encodeURIComponent(input.id)}/teams/${encodeURIComponent(input.teamId)}/members/${encodeURIComponent(input.userId)}`,
        { method: "DELETE" },
    );
    if (!response.ok) {
        throw new Error(
            `Failed to remove team member: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return TeamSchema.parse(json);
}

export interface TeamQueueInput {
    readonly id: string;
    readonly teamId: string;
    readonly queueId: string;
}

export async function pushTeamToQueue(
    client: ApiClient,
    input: TeamQueueInput,
): Promise<TeamQueuePushResponse> {
    const response = await client.fetch(
        `/management-groups/${encodeURIComponent(input.id)}/teams/${encodeURIComponent(input.teamId)}/push-to-queue/${encodeURIComponent(input.queueId)}`,
        { method: "POST" },
    );
    if (!response.ok) {
        throw new Error(
            `Failed to push team to queue: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return TeamQueuePushResponseSchema.parse(json);
}

export async function withdrawTeamFromQueue(
    client: ApiClient,
    input: TeamQueueInput,
): Promise<TeamQueueWithdrawResponse> {
    const response = await client.fetch(
        `/management-groups/${encodeURIComponent(input.id)}/teams/${encodeURIComponent(input.teamId)}/withdraw-from-queue/${encodeURIComponent(input.queueId)}`,
        { method: "POST" },
    );
    if (!response.ok) {
        throw new Error(
            `Failed to withdraw team from queue: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return TeamQueueWithdrawResponseSchema.parse(json);
}
